/**
 * Admin Users List
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Shield,
  ShieldCheck,
  MoreHorizontal,
  Building2,
  Eye,
  Pencil,
  Trash2,
  ArrowUp,
} from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";

// Convex ID type helper
type Id<T extends string> = string & { __tableName: T };
import { useDateFormat } from "../../hooks/useDateFormat";
import { useSession } from "../../lib/auth";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  Badge,
  Card,
  CardContent,
  Skeleton,
  Avatar,
  AvatarFallback,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from "@jourfx/ui";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge, type StatusType } from "../../components/StatusBadge";
import { EmptyState } from "../../components/EmptyState";

interface AdminUser {
  _id: string;
  email: string;
  name: string;
  role: "administrator" | "super_admin";
  organizationId?: string;
  organizationName?: string;
  status: "active" | "inactive" | "locked";
  lastLoginAt?: number;
  createdAt: number;
}

function getAdminRoleLabel(
  t: (key: string) => string,
  role: AdminUser["role"]
): string {
  if (role === "super_admin") {
    return t("roles.super_admin");
  }
  return t("roles.administrator");
}

function AdminsListContent() {
  const { t } = useTranslation("admins");
  const { t: tc } = useTranslation("common");
  const { formatShortDate } = useDateFormat();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isSuperAdmin } = useSession();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<AdminUser | null>(null);
  const [adminToPromote, setAdminToPromote] = useState<AdminUser | null>(null);

  const admins = useQuery(api.admin.admins.list) as AdminUser[] | undefined;
  const removeAdmin = useMutation(api.admin.admins.remove);
  const promoteAdmin = useMutation(api.admin.admins.promote);

  const handleViewClick = (adminId: string) => {
    navigate({ to: "/admins/$adminId", params: { adminId } });
  };

  const handleEditClick = (adminId: string) => {
    navigate({ to: "/admins/$adminId", params: { adminId } });
  };

  const handleDeleteClick = (admin: AdminUser) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const handlePromoteClick = (admin: AdminUser) => {
    setAdminToPromote(admin);
    setPromoteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    try {
      await removeAdmin({ id: adminToDelete._id as Id<"adminUsers"> });
      toast({
        title: t("toasts.deleted.title"),
        description: t("toasts.deleted.description", { name: adminToDelete.name }),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleConfirmPromote = async () => {
    if (!adminToPromote) return;
    try {
      await promoteAdmin({ adminId: adminToPromote._id as Id<"adminUsers"> });
      toast({
        title: t("toasts.promoted.title"),
        description: t("toasts.promoted.description", { name: adminToPromote.name }),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setPromoteDialogOpen(false);
      setAdminToPromote(null);
    }
  };

  if (admins === undefined) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-56" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <Skeleton className="h-5 w-5" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-8 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="divide-y divide-border">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <Button asChild>
            <Link to="/admins/new">
              <Plus className="mr-2 h-4 w-4" />
              {t("newAdmin")}
            </Link>
          </Button>
        }
      />

      {/* Stats */}
      <div className={`grid gap-4 ${isSuperAdmin ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold tabular-nums text-foreground">{admins.length}</p>
                <p className="text-sm text-muted-foreground">{t("stats.total")}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <Shield className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
        {isSuperAdmin && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold tabular-nums text-foreground">
                    {admins.filter((a) => a.role === "super_admin").length}
                  </p>
                  <p className="text-sm text-muted-foreground">{t("stats.superAdmins")}</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold tabular-nums text-foreground">
                  {admins.filter((a) => a.role === "administrator").length}
                </p>
                <p className="text-sm text-muted-foreground">{t("stats.administrators")}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-info-muted">
                <Building2 className="h-5 w-5 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.name")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.role")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.organization")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.status")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.lastLogin")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {admins.map((admin) => (
              <tr
                key={admin._id}
                className="group transition-colors hover:bg-muted/50"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className={admin.role === "super_admin" ? "bg-primary/10 text-primary" : "bg-muted"}>
                        {admin.name.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{admin.name}</span>
                        {admin.role === "super_admin" && (
                          <ShieldCheck className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{admin.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Badge
                    variant={admin.role === "super_admin" ? "default" : "secondary"}
                    className={admin.role === "super_admin" ? "bg-primary/10 text-primary hover:bg-primary/20" : ""}
                  >
                    {getAdminRoleLabel(t, admin.role)}
                  </Badge>
                </td>
                <td className="px-6 py-4 text-muted-foreground">
                  {admin.organizationName || "-"}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={admin.status as StatusType} />
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  {admin.lastLoginAt
                    ? formatShortDate(admin.lastLoginAt)
                    : t("table.never")}
                </td>
                <td className="px-6 py-4 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">{t("actions.openMenu")}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewClick(admin._id)}>
                        <Eye className="mr-2 h-4 w-4" />
                        {t("actions.viewDetails")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEditClick(admin._id)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {tc("actions.edit")}
                      </DropdownMenuItem>
                      {isSuperAdmin && admin.role === "administrator" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handlePromoteClick(admin)}>
                            <ArrowUp className="mr-2 h-4 w-4" />
                            {t("actions.promoteToSuperAdmin")}
                          </DropdownMenuItem>
                        </>
                      )}
                      {isSuperAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDeleteClick(admin)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {tc("actions.delete")}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {admins.length === 0 && (
        <EmptyState
          icon={Shield}
          title={t("empty.title")}
          description={t("empty.description")}
          action={{
            label: t("newAdmin"),
            onClick: () => navigate({ to: "/admins/new" }),
            icon: Plus,
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteDialog.description", { name: adminToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Promote Confirmation Dialog */}
      <AlertDialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("promoteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("promoteDialog.description", { name: adminToPromote?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("promoteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPromote}>
              {t("promoteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function AdminsList() {
  return <AdminsListContent />;
}
